import { ref } from 'vue'

// Coreografia d'ingresso + effetti del titolo della PWA (header pubblico).
//
// Sequenza (solo alla prima apertura, sulla Home, se non reduced-motion):
//   0 → 1s : schermo "pulito" (titolo e resto UI nascosti) → evita il flash di
//            caricamento così l'effetto si nota;
//   1 → 2s : compare SOLO il titolo, con blur→nitido + lettere che salgono
//            sfalsate (gestito da AppTitle.vue via `titleVisible`);
//   da 2s  : il resto della UI compare in fade-in (HomeView via `uiVeiled`).
//
// Oltre all'ingresso, lo "sparkle" è un hint periodico che il titolo è cliccabile
// (apre la modale About): prima apparizione dopo un ritardo casuale, poi
// ripetizione rara con policy ibrida (cambi pagina + cooldown temporale).
//
// Stato singleton di modulo (come useFollowedTeam): sopravvive a unmount/remount
// della Home quando si naviga a /match/:id e si torna.

const INTRO_TITLE_DELAY_MS = 1000 // il titolo compare dopo 1s (niente flash prima)
const TITLE_REVEAL_MS = 1000 // durata effetto titolo → UI svelata a 1s+1s = 2s
const FIRST_DELAY_MIN_MS = 20000 // prima apparizione sparkle: 20s…
const FIRST_DELAY_MAX_MS = 60000 // …60s dopo l'apertura
const SPARKLE_DURATION_MS = 3500 // durata effetto sparkle
const RETRIGGER_MIN_NAV = 8 // cambi pagina minimi per poter ripetere
const RETRIGGER_MIN_MS = 600000 // tempo minimo (10 min) dall'ultima volta
const HOME_ROUTE_NAME = 'public-home'

// Stato d'ingresso: default "animato" (titolo nascosto, UI velata). In reduced
// motion initTitleHint li forza subito a visibile/non-velato.
const titleVisible = ref(false)
const uiVeiled = ref(true)
// Stato sparkle.
const sparkleActive = ref(false)

let initialized = false
let navCount = 0
let lastFireAt = 0
let titleTimer = null
let uiTimer = null
let firstTimer = null
let sparkleTimer = null

function prefersReducedMotion () {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

// True quando dobbiamo riprodurre l'intro animata. Usato da HomeView/AppTitle per
// decidere se velare/animare (valutato al setup del componente, matchMedia live).
export function shouldAnimateIntro () {
  return !prefersReducedMotion()
}

function firstDelayMs () {
  return FIRST_DELAY_MIN_MS + Math.random() * (FIRST_DELAY_MAX_MS - FIRST_DELAY_MIN_MS)
}

// Accende lo sparkle per SPARKLE_DURATION_MS, poi lo spegne. Registra il momento
// (per il cooldown) e azzera il contatore di navigazioni.
function fire () {
  if (sparkleActive.value) return
  sparkleActive.value = true
  lastFireAt = Date.now()
  navCount = 0
  clearTimeout(sparkleTimer)
  sparkleTimer = setTimeout(() => { sparkleActive.value = false }, SPARKLE_DURATION_MS)
}

// Idempotente: va chiamato una sola volta (alla prima mount della Home). Con
// reduced-motion mostra tutto subito senza animazioni né sparkle. Altrimenti
// avvia la coreografia d'ingresso, il primo sparkle e la ripetizione ibrida.
export function initTitleHint (router) {
  if (initialized) return
  initialized = true

  if (prefersReducedMotion()) {
    titleVisible.value = true
    uiVeiled.value = false
    return
  }

  titleTimer = setTimeout(() => { titleVisible.value = true }, INTRO_TITLE_DELAY_MS)
  uiTimer = setTimeout(() => { uiVeiled.value = false }, INTRO_TITLE_DELAY_MS + TITLE_REVEAL_MS)

  lastFireAt = Date.now() // baseline del cooldown = apertura app
  firstTimer = setTimeout(fire, firstDelayMs())

  router.afterEach((to) => {
    navCount += 1
    const elapsed = Date.now() - lastFireAt
    const onHome = to.name === HOME_ROUTE_NAME
    if (onHome && navCount >= RETRIGGER_MIN_NAV && elapsed >= RETRIGGER_MIN_MS) {
      fire()
    }
  })
}

export function useTitleHint () {
  return { titleVisible, uiVeiled, sparkleActive, initTitleHint }
}

// Solo per i test: reimposta lo stato singleton di modulo (default "animato").
export function __resetTitleHint () {
  initialized = false
  navCount = 0
  lastFireAt = 0
  titleVisible.value = false
  uiVeiled.value = true
  sparkleActive.value = false
  clearTimeout(titleTimer)
  clearTimeout(uiTimer)
  clearTimeout(firstTimer)
  clearTimeout(sparkleTimer)
}
